import { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { LightTooltip } from "../../../../../components/LightTooltip";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import { INSTALL_FILTER_MAPPINGS, FilterMappingKey } from "../../../constants";

interface FilterIndicatorProps {
  filterKey: FilterMappingKey;
}

export const FilterIndicator = ({ filterKey }: FilterIndicatorProps) => {
  const installFilters = useCompanyPageStore((state) => state.installFilters);
  const appliedFilters = INSTALL_FILTER_MAPPINGS[filterKey];

  // Memoize computations to prevent unnecessary re-renders
  const hasActiveFilters = useMemo(
    () =>
      installFilters.countryAlpha2s.length > 0 ||
      installFilters.productIds.length > 0 ||
      installFilters.vendorIds.length > 0 ||
      installFilters.productCategoryIds.length > 0 ||
      installFilters.productAttributeIds.length > 0 ||
      installFilters.includeCorporateDescendants,
    [installFilters]
  );

  const activeFiltersList = useMemo(() => {
    const activeFilters = [];

    if (installFilters.countryAlpha2s.length > 0 && appliedFilters.geography) {
      activeFilters.push("Geography (Country)");
    }
    if (installFilters.productIds.length > 0 && appliedFilters.products) {
      activeFilters.push("Products");
    }
    if (installFilters.vendorIds.length > 0 && appliedFilters.vendors) {
      activeFilters.push("Vendors");
    }
    if (
      installFilters.productCategoryIds.length > 0 &&
      appliedFilters.categories
    ) {
      activeFilters.push("Product Categories");
    }
    if (
      installFilters.productAttributeIds.length > 0 &&
      appliedFilters.attributes
    ) {
      activeFilters.push("Product Attributes");
    }
    if (
      installFilters.includeCorporateDescendants &&
      appliedFilters.corporate
    ) {
      activeFilters.push("Entire organization");
    }

    return activeFilters;
  }, [installFilters, appliedFilters]);

  const noFiltersApplied = useMemo(
    () => Object.values(appliedFilters).every((v) => v === false),
    [appliedFilters]
  );

  const tooltipContent = useMemo(
    () => (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="body2" fontWeight="bold" mb={1}>
          How is this data filtered?
        </Typography>

        {noFiltersApplied ? (
          <Typography variant="body2" color="text.secondary">
            Filters do not impact this section
          </Typography>
        ) : activeFiltersList.length > 0 ? (
          activeFiltersList.map((filter, index) => (
            <Typography key={index} variant="body2" sx={{ mb: 0.3 }}>
              • {filter}
            </Typography>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No active filters affecting this section
          </Typography>
        )}
      </Box>
    ),
    [noFiltersApplied, activeFiltersList]
  );

  // Only show the indicator if there are active filters
  if (!hasActiveFilters) {
    return null;
  }

  return (
    <LightTooltip title={tooltipContent}>
      <Box
        sx={{
          ml: 1,
          display: "flex",
          alignItems: "center",
          cursor: "help",
          "@keyframes pulse": {
            "0%": {
              transform: "scale(1)",
              opacity: 1,
            },
            "50%": {
              transform: "scale(1.1)",
              opacity: 0.8,
            },
            "100%": {
              transform: "scale(1)",
              opacity: 1,
            },
          },
          "&:hover .MuiSvgIcon-root": {
            animation: "none",
            transform: "scale(1.15)",
          },
        }}
      >
        <FilterAltIcon
          sx={{
            fontSize: 22,
            color: (theme) => theme.palette.primary.main,
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
            animation: "pulse 2s ease-in-out infinite",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </Box>
    </LightTooltip>
  );
};
